import React, { createContext, useContext, useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Top 5 currencies offered in the site menu. Base currency is EUR (rate 1).
// `rate` values are sensible fallbacks; live rates are fetched on mount from the backend.
const CURRENCIES = {
  EUR: { symbol: '\u20AC', name: 'Euro', rate: 1 },
  USD: { symbol: '$', name: 'US Dollar', rate: 1.08 },
  GBP: { symbol: '\u00A3', name: 'British Pound', rate: 0.86 },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', rate: 0.97 },
  CNY: { symbol: '\u00A5', name: 'Chinese Yuan', rate: 7.85 },
};

const CurrencyContext = createContext();

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem('preferred_currency') || 'EUR');
  const [rates, setRates] = useState(CURRENCIES);

  useEffect(() => {
    localStorage.setItem('preferred_currency', currency);
  }, [currency]);

  // Fetch live EUR-based exchange rates once on mount; merge into our currency set.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/currency/rates?base=EUR`)
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data || !data.rates) return;
        setRates(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(code => {
            const live = data.rates[code];
            if (typeof live === 'number' && live > 0) next[code] = { ...next[code], rate: live };
          });
          return next;
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const convert = (eurAmount) => {
    const num = typeof eurAmount === 'string' ? parseFloat(eurAmount.replace(/[^0-9.]/g, '')) : eurAmount;
    if (isNaN(num)) return eurAmount;
    const converted = Math.round(num * (rates[currency]?.rate || 1));
    return converted.toLocaleString();
  };

  const symbol = rates[currency]?.symbol || '\u20AC';

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convert, symbol, currencies: rates }}>
      {children}
    </CurrencyContext.Provider>
  );
}
